import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface FormFieldProps {
    id: string
    label: string
    error?: string
    hint?: string
    optional?: boolean
    className?: string
    children: ReactNode
}

// Shared look for every text input, select and textarea on the site
export const inputClass = cn(
    'block w-full rounded-[3px] border border-bark/25 bg-paper/70 px-4 py-3 text-[1rem] text-bark',
    'placeholder:text-bark-mute transition-colors duration-300',
    'hover:border-bark/45 focus:border-bark focus:bg-paper focus:outline-none focus:ring-2 focus:ring-bark/15',
    'aria-[invalid=true]:border-pomegranate aria-[invalid=true]:ring-pomegranate/15'
)

export const FormField: FC<FormFieldProps> = ({ id, label, error, hint, optional = false, className, children }): ReactNode => {
    return (
        <div className={className}>
            <label htmlFor={id} className='mb-2 flex items-baseline justify-between gap-3 text-sm font-medium text-bark'>
                <span>{label}</span>
                {optional && <span className='text-[0.8rem] font-normal text-bark-soft'>Optional</span>}
            </label>
            {children}
            {error ? (
                <p id={`${id}-error`} role='alert' className='mt-2 text-sm text-pomegranate'>
                    {error}
                </p>
            ) : hint ? (
                <p id={`${id}-hint`} className='mt-2 text-sm text-bark-soft'>
                    {hint}
                </p>
            ) : null}
        </div>
    )
}

interface HoneypotProps {
    value: string
    onChange: (value: string) => void
}

// Hidden from people, irresistible to spam bots
export const Honeypot: FC<HoneypotProps> = ({ value, onChange }): ReactNode => {
    return (
        <div aria-hidden='true' className='absolute -left-[9999px] h-px w-px overflow-hidden'>
            <label htmlFor='company'>Company</label>
            <input
                id='company'
                name='company'
                type='text'
                tabIndex={-1}
                autoComplete='off'
                value={value}
                onChange={event => onChange(event.target.value)}
            />
        </div>
    )
}

export const describedBy = (id: string, error?: string, hint?: string): string | undefined => {
    if (error) return `${id}-error`
    if (hint) return `${id}-hint`
    return undefined
}
